// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — Reports Gallery
// ---------------------------------------------------------------------------
// SavedViews.js turns a query into a saved artefact. This screen is the wrapper
// a consultant actually hands over: a report is an OWNED, FAVOURITABLE,
// SHAREABLE deliverable filed under an ENGAGEMENT USE CASE, not a chart type.
//
// Three kinds of report live side by side:
//   1. built-ins      — the report screens that ship with Primordia (owner
//                       "Primordia", the way LeanIX shows "LeanIX Default")
//   2. saved views /  — whatever the user built in Saved Views & Reports; read
//      saved reports    STRAIGHT out of that feature's own storage
//   3. saved configs  — the "View:" convention: one base report saved several
//                       times under different colour measures, e.g.
//                       "Application Landscape — View: Obsolescence risk"
//
// STORAGE — deliberately parasitic, never parallel:
//   read-only : primordia.views.<tenantId>          (owned by SavedViews.js)
//   read/write: primordia.views.<tenantId>.gallery  (owned by this screen)
// The gallery never writes into SavedViews' key, so nothing it does can corrupt
// or reshuffle what SavedViews reads back. Its own key is a sibling in the same
// primordia.views.<tenant> family and is tenant-scoped for the same reason:
// isolation is the product, so a report saved in a client workspace is readable
// only inside that workspace. There is no cross-tenant sharing and there will
// not be one — "shared" here means "shared with others in THIS workspace".
//
// HONESTY RULES baked in:
//   * owner is the signed-in creator's email or nothing at all — never a guess.
//     Legacy SavedViews records carry no owner, so they read "not recorded".
//   * a card only shows the shared badge when the record actually says shared.
//   * a saved configuration whose base report no longer exists is listed, but
//     flagged broken and refuses to open.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Checkbox, Chip, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  Grid, IconButton, InputAdornment, List, ListItemButton, ListItemText, Menu, MenuItem,
  Paper, Radio, RadioGroup, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsIcon from '@mui/icons-material/Groups';
import LabelIcon from '@mui/icons-material/Label';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { exportCsv } from './exports';
import { getSession } from './auth';
import store from './store';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const primaryBtn = { bgcolor: BLUE, '&:hover': { bgcolor: NAVY } };

// ---------------------------------------------------------------------------
// Engagement use cases — how a consultant scopes an engagement, which is why
// this and not chart type is the primary axis of the gallery.
// ---------------------------------------------------------------------------
const USE_CASES = [
  'Application Rationalization',
  'Portfolio Assessment',
  'Obsolescence & Technology Risk',
  'Integration & Data Architecture',
  'Cost & FinOps',
  'Transformation Roadmap',
  'Infrastructure & Migration Readiness',
];
const UNASSIGNED = 'Not assigned to a use case';

// Colour measures for the "View:" convention. Saving the same base report under
// several of these is exactly how a LeanIX tenant ends up with
// "Application Landscape — View: Obsolescence risk".
const MEASURES = [
  'Obsolescence risk',
  'Lifecycle state',
  'Business criticality',
  'Functional fit',
  'Technical fit',
  '6R disposition',
  'Cloud readiness',
  'Annual cost (TCO)',
  'Hosting type',
  'Data classification',
  'Wave / target date',
  'No colour measure',
];

// ---------------------------------------------------------------------------
// Built-in report catalogue. `key` is the CmdbApp view key handed back through
// onOpenView(viewKey, config) — navigation is wired centrally, this screen only
// names the destination. Each entry declares its own use cases so the grouping
// stays data-driven; a report may sit in more than one group.
// ---------------------------------------------------------------------------
const BUILT_INS = [
  {
    key: 'landscape',
    title: 'Application Landscape',
    description: 'Heat-mapped landscape of the application portfolio by capability cell, coloured by the measure you choose.',
    type: 'Heat map',
    useCases: ['Application Rationalization', 'Portfolio Assessment'],
  },
  {
    key: 'matrix',
    title: 'Matrix & Circle Map',
    description: 'Two-dimensional matrix and circle map for cross-tabulating applications against any two dimensions.',
    type: 'Matrix',
    useCases: ['Portfolio Assessment', 'Application Rationalization'],
  },
  {
    key: 'portfolio',
    title: 'Portfolio & Radar',
    description: 'Functional-fit versus technical-fit portfolio quadrant with a technology radar for the standards conversation.',
    type: 'Chart',
    useCases: ['Portfolio Assessment', 'Application Rationalization'],
  },
  {
    key: 'obsolescence',
    title: 'Obsolescence & Technology Risk',
    description: 'End-of-life and end-of-support exposure across applications, versions and the underlying technology stack.',
    type: 'Risk report',
    useCases: ['Obsolescence & Technology Risk', 'Application Rationalization'],
  },
  {
    key: 'roadmap',
    title: 'Migration Roadmap',
    description: 'Timeline of lifecycle transitions and target dates — the roadmap slide for the steering committee.',
    type: 'Timeline',
    useCases: ['Transformation Roadmap'],
  },
  {
    key: 'waveplanner',
    title: 'Wave Planner (dependency-ordered)',
    description: 'Migration waves sequenced by interface and infrastructure dependency rather than by wishful grouping.',
    type: 'Plan',
    useCases: ['Transformation Roadmap', 'Infrastructure & Migration Readiness'],
  },
  {
    key: 'mindmap',
    title: 'Relationship Mind Map',
    description: 'Interactive mind map of how a fact sheet relates to everything around it.',
    type: 'Network',
    useCases: ['Integration & Data Architecture'],
  },
  {
    key: 'relationmap',
    title: 'Relation Map (printable)',
    description: 'Print-ready relationship diagram of applications, interfaces and infrastructure.',
    type: 'Network',
    useCases: ['Integration & Data Architecture'],
  },
  {
    key: 'interfaces',
    title: 'Interface Engines',
    description: 'Interface inventory by engine, protocol and direction — the integration surface you have to carry across.',
    type: 'Table',
    useCases: ['Integration & Data Architecture', 'Infrastructure & Migration Readiness'],
  },
  {
    key: 'readiness',
    title: 'Cutover Readiness & Gaps',
    description: 'Readiness scorecard exposing the data gaps that will stop a cutover decision being made.',
    type: 'Scorecard',
    useCases: ['Infrastructure & Migration Readiness', 'Transformation Roadmap'],
  },
  {
    key: 'cost',
    title: 'Cost Modelling',
    description: 'Run-rate and total cost of ownership modelling across the portfolio.',
    type: 'Cost model',
    useCases: ['Cost & FinOps', 'Portfolio Assessment'],
  },
  {
    key: 'rightsizing',
    title: 'Rightsizing',
    description: 'Utilisation-driven instance and storage rightsizing with the resulting cost delta.',
    type: 'Cost model',
    useCases: ['Cost & FinOps', 'Infrastructure & Migration Readiness'],
  },
  {
    key: 'servermap',
    title: 'Server → Deployment Mapping',
    description: 'Which servers carry which application deployments — the join that makes a migration wave real.',
    type: 'Table',
    useCases: ['Infrastructure & Migration Readiness', 'Integration & Data Architecture'],
  },
  {
    key: 'csg',
    title: 'CSG Target State (Epic)',
    description: 'Epic client system guide target-state sizing and topology.',
    type: 'Design',
    useCases: ['Infrastructure & Migration Readiness', 'Transformation Roadmap'],
  },
  {
    key: 'snapshots',
    title: 'Progress Since Kickoff',
    description: 'Snapshot comparison showing what the discovery has actually moved since the engagement started.',
    type: 'Comparison',
    useCases: ['Portfolio Assessment'],
  },
  {
    key: 'benchmarks',
    title: 'Cross-client Benchmarks',
    description: 'Anonymised benchmarks of this portfolio against the wider Optimum client base.',
    type: 'Chart',
    useCases: ['Portfolio Assessment', 'Application Rationalization'],
  },
  {
    key: 'views',
    title: 'Saved Views & Report Builder',
    description: 'Build a filtered, sorted, column-picked view or a grouped chart, then save it as its own artefact.',
    type: 'Report builder',
    useCases: ['Portfolio Assessment', 'Application Rationalization'],
  },
  {
    key: 'dashboard',
    title: 'Dashboards',
    description: 'The at-a-glance dashboard for the workspace.',
    type: 'Dashboard',
    useCases: ['Portfolio Assessment'],
  },
  {
    key: 'inventory',
    title: 'Inventory',
    description: 'The faceted fact sheet inventory every other report is drawn from.',
    type: 'Table',
    useCases: ['Application Rationalization', 'Portfolio Assessment'],
  },
  {
    key: 'catalogintel',
    title: 'Cross-client App Intelligence',
    description: 'Named cross-client application intelligence. Master workspace only.',
    type: 'Chart',
    useCases: ['Application Rationalization', 'Portfolio Assessment'],
    masterOnly: true,
  },
  {
    key: 'catalog',
    title: 'Master Catalog',
    description: 'The Optimum-owned canonical application catalogue. Master workspace only.',
    type: 'Table',
    useCases: ['Application Rationalization'],
    masterOnly: true,
  },
];

const BUILT_IN_BY_KEY = BUILT_INS.reduce((m, b) => { m[b.key] = b; return m; }, {});

const SHELVES = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'My Reports' },
  { key: 'shared', label: 'Shared with me' },
  { key: 'fav', label: 'Favorites' },
  { key: 'recent', label: 'Recently Used' },
];

const SOURCE_LABEL = {
  builtin: 'Built-in report',
  'saved-view': 'Saved view',
  'saved-report': 'Saved report',
  config: 'Saved configuration',
};

const PRIMORDIA_OWNER = 'Primordia';
const RECENT_LIMIT = 12;

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------
const savedViewsKey = (tenantId) => `primordia.views.${tenantId}`;
const galleryKey = (tenantId) => `primordia.views.${tenantId}.gallery`;

const EMPTY_GALLERY = { configs: [], labels: {}, users: {} };

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

// SavedViews owns this array; we only ever read it.
function readSavedItems(tenantId) {
  const parsed = readJson(savedViewsKey(tenantId));
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((v) => v && v.id && (!v.tenantId || v.tenantId === tenantId));
}

function normalizeGallery(blob) {
  const b = (blob && typeof blob === 'object' && !Array.isArray(blob)) ? blob : {};
  return {
    configs: Array.isArray(b.configs) ? b.configs : [],
    labels: (b.labels && typeof b.labels === 'object' && !Array.isArray(b.labels)) ? b.labels : {},
    users: (b.users && typeof b.users === 'object' && !Array.isArray(b.users)) ? b.users : {},
  };
}

const uid = () => `cfg-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));

// Use-case display order: declared list first, then anything a user invented,
// then the unassigned bucket last.
function orderUseCases(list) {
  const known = USE_CASES.filter((u) => list.indexOf(u) !== -1);
  const extra = list.filter((u) => USE_CASES.indexOf(u) === -1 && u !== UNASSIGNED).sort();
  return known.concat(extra).concat(list.indexOf(UNASSIGNED) !== -1 ? [UNASSIGNED] : []);
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
function ReportCard({ item, favorite, onOpen, onMenu, onToggleFavorite }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        borderColor: item.broken ? '#D0021B' : undefined,
        opacity: item.broken ? 0.85 : 1,
      }}
    >
      <CardActionArea
        disabled={item.broken}
        onClick={() => onOpen(item)}
        sx={{ flex: 1, alignItems: 'stretch', justifyContent: 'flex-start' }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Typography sx={{ flex: 1, fontWeight: 700, color: HEAD, fontSize: 15, lineHeight: 1.3 }}>
              {item.title}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label={item.type} sx={{ height: 20, fontSize: 10, bgcolor: '#eef4fb', color: NAVY, fontWeight: 700 }} />
            <Chip
              size="small"
              icon={item.shared ? <GroupsIcon sx={{ fontSize: 13 }} /> : <LockIcon sx={{ fontSize: 13 }} />}
              label={item.shared ? 'this workspace' : 'private to owner'}
              sx={{ height: 20, fontSize: 10 }}
            />
            {item.broken && (
              <Chip
                size="small"
                icon={<ReportProblemIcon sx={{ fontSize: 13 }} />}
                label="broken"
                sx={{ height: 20, fontSize: 10, bgcolor: '#D0021B', color: '#fff', fontWeight: 700 }}
              />
            )}
          </Stack>

          <Typography variant="body2" sx={{ color: GREY, mt: 1, minHeight: 40 }}>
            {item.description || 'No description recorded.'}
          </Typography>

          {item.broken && (
            <Typography variant="caption" sx={{ color: '#D0021B', display: 'block', mt: 0.5, fontWeight: 700 }}>
              {item.brokenReason}
            </Typography>
          )}

          {!!item.useCases.length && (
            <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
              {item.useCases.map((u) => (
                <Chip key={u} size="small" variant="outlined" label={u} sx={{ height: 19, fontSize: 9.5, color: GREY }} />
              ))}
            </Stack>
          )}
        </CardContent>
      </CardActionArea>

      <Divider />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap variant="caption" sx={{ display: 'block', color: NAVY, fontWeight: 700 }}>
            {item.ownerLabel}
          </Typography>
          <Typography noWrap variant="caption" sx={{ display: 'block', color: GREY }}>
            {SOURCE_LABEL[item.source]}
            {item.updated ? ` · updated ${fmtDate(item.updated)}` : ' · updated not recorded'}
          </Typography>
        </Box>
        <Tooltip title={favorite ? 'Remove from my favorites' : 'Add to my favorites'}>
          <IconButton size="small" onClick={() => onToggleFavorite(item)}>
            {favorite
              ? <StarIcon sx={{ fontSize: 18, color: '#F5A623' }} />
              : <StarBorderIcon sx={{ fontSize: 18, color: GREY }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="More">
          <IconButton size="small" onClick={(e) => onMenu(e.currentTarget, item)}>
            <MoreVertIcon sx={{ fontSize: 18, color: GREY }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
export default function ReportsGallery({ tenantId, onOpenView }) {
  const [saved, setSaved] = useState([]);            // SavedViews' own records (read-only)
  const [gallery, setGallery] = useState(EMPTY_GALLERY);
  const [fsCount, setFsCount] = useState(null);
  const [msg, setMsg] = useState('');

  const [shelf, setShelf] = useState('all');
  const [useCase, setUseCase] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [q, setQ] = useState('');

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [cfgDialog, setCfgDialog] = useState(null);   // draft config record
  const [labelDialog, setLabelDialog] = useState(null);

  // Identity. getSession() re-validates the JWT expiry on every call, so an
  // expired session correctly yields "no signed-in user" instead of a stale one.
  const session = getSession();
  const myEmail = (session && session.email) ? session.email : '';
  // Favourites/recents are per user. With no session there is no user, so they
  // are filed under a browser-local bucket and the UI says exactly that.
  const userKey = myEmail || '(this browser)';
  const isMaster = tenantId === 'optimum';

  // ---- load ---------------------------------------------------------------
  useEffect(() => {
    setSaved(readSavedItems(tenantId));
    setGallery(normalizeGallery(readJson(galleryKey(tenantId))));
    setShelf('all');
    setUseCase('all');
    setTypeFilter('all');
  }, [tenantId]);

  useEffect(() => {
    let live = true;
    store.listFactSheets(tenantId)
      .then((rows) => { if (live) setFsCount(rows.length); })
      .catch(() => { if (live) setFsCount(null); });
    return () => { live = false; };
  }, [tenantId]);

  const persistGallery = useCallback((next) => {
    setGallery(next);
    try {
      localStorage.setItem(galleryKey(tenantId), JSON.stringify(next));
    } catch (e) { /* quota — the in-memory copy still works for this session */ }
  }, [tenantId]);

  const myShelfData = useMemo(() => {
    const u = gallery.users[userKey] || {};
    return {
      favorites: Array.isArray(u.favorites) ? u.favorites : [],
      recent: Array.isArray(u.recent) ? u.recent : [],
    };
  }, [gallery.users, userKey]);

  const favSet = useMemo(() => new Set(myShelfData.favorites), [myShelfData.favorites]);
  const recentOrder = useMemo(() => {
    const m = {};
    myShelfData.recent.forEach((r, i) => { if (r && r.id && m[r.id] === undefined) m[r.id] = i; });
    return m;
  }, [myShelfData.recent]);

  const writeUser = useCallback((patch) => {
    const users = { ...gallery.users };
    users[userKey] = { ...myShelfData, ...patch };
    persistGallery({ ...gallery, users });
  }, [gallery, userKey, myShelfData, persistGallery]);

  // ---- unified item list --------------------------------------------------
  const items = useMemo(() => {
    const labels = gallery.labels || {};
    const extraFor = (id) => (Array.isArray(labels[id]) ? labels[id] : []);
    const out = [];

    BUILT_INS.forEach((b) => {
      if (b.masterOnly && !isMaster) return;
      out.push({
        id: `builtin:${b.key}`,
        source: 'builtin',
        viewKey: b.key,
        title: b.title,
        description: b.description,
        type: b.type,
        useCases: uniq(b.useCases.concat(extraFor(`builtin:${b.key}`))),
        ownerEmail: '',
        ownerLabel: PRIMORDIA_OWNER,
        // Built-ins ship with the product: everyone in this workspace has them.
        shared: true,
        updated: null,
        config: null,
        broken: false,
        brokenReason: '',
        canEdit: false,
        canDelete: false,
      });
    });

    saved.forEach((v) => {
      const isReport = v.kind === 'report';
      // SavedViews records have never carried an owner. Guessing one would be a
      // lie on a deliverable, so unattributed records say so.
      const owner = v.owner || v.createdBy || '';
      const declared = Array.isArray(v.useCases) ? v.useCases : [];
      out.push({
        id: v.id,
        source: isReport ? 'saved-report' : 'saved-view',
        viewKey: 'views',
        title: v.name || '(unnamed)',
        description: v.description || '',
        type: isReport
          ? `Saved report · ${v.chartType || 'chart'}`
          : `Saved view · ${v.type || 'fact sheets'}`,
        useCases: uniq(declared.concat(extraFor(v.id))),
        ownerEmail: owner,
        ownerLabel: owner || 'Owner not recorded',
        shared: !!v.shared,
        updated: v.updatedAt || v.createdAt || null,
        config: v,
        broken: false,
        brokenReason: '',
        // Sharing on these records lives in SavedViews' own store, which this
        // screen never writes to.
        canEdit: false,
        canDelete: false,
      });
    });

    (gallery.configs || []).forEach((c) => {
      if (!c || !c.id) return;
      const base = BUILT_IN_BY_KEY[c.viewKey];
      let broken = false;
      let reason = '';
      if (!base) {
        broken = true;
        reason = `Base report "${c.viewKey}" no longer exists in this build.`;
      } else if (base.masterOnly && !isMaster) {
        broken = true;
        reason = 'Base report is only available in the master workspace.';
      }
      out.push({
        id: c.id,
        source: 'config',
        viewKey: c.viewKey,
        title: c.name || '(unnamed configuration)',
        description: c.description || (base ? `Configuration of ${base.title}.` : ''),
        type: base ? `${base.type} · configuration` : 'Configuration',
        useCases: uniq((Array.isArray(c.useCases) ? c.useCases : []).concat(extraFor(c.id))),
        ownerEmail: c.owner || '',
        ownerLabel: c.owner || 'Owner not recorded',
        shared: !!c.shared,
        updated: c.updatedAt || c.createdAt || null,
        config: c,
        broken,
        brokenReason: reason,
        // Only the owner may re-author or unshare someone's deliverable. An
        // unattributed record (saved with no session) stays editable, otherwise
        // it would be stranded forever.
        canEdit: !c.owner || c.owner === myEmail,
        canDelete: !c.owner || c.owner === myEmail,
        record: c,
      });
    });

    return out;
  }, [saved, gallery.configs, gallery.labels, isMaster, myEmail]);

  const userItems = useMemo(() => items.filter((i) => i.source !== 'builtin'), [items]);

  // ---- filtering ----------------------------------------------------------
  const matchesQuery = useCallback((item) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return `${item.title} ${item.description} ${item.ownerLabel}`.toLowerCase().indexOf(needle) !== -1;
  }, [q]);

  const searched = useMemo(() => items.filter(matchesQuery), [items, matchesQuery]);

  const onShelf = useCallback((item, key) => {
    switch (key) {
      case 'mine': return !!item.ownerEmail && item.ownerEmail === myEmail;
      case 'shared': return item.source !== 'builtin' && item.shared && item.ownerEmail !== myEmail;
      case 'fav': return favSet.has(item.id);
      case 'recent': return recentOrder[item.id] !== undefined;
      default: return true;
    }
  }, [myEmail, favSet, recentOrder]);

  const shelfCounts = useMemo(() => {
    const m = {};
    SHELVES.forEach((s) => { m[s.key] = searched.filter((i) => onShelf(i, s.key)).length; });
    return m;
  }, [searched, onShelf]);

  const base = useMemo(() => searched.filter((i) => onShelf(i, shelf)), [searched, onShelf, shelf]);

  const useCaseCounts = useMemo(() => {
    const m = {};
    base.forEach((i) => {
      const list = i.useCases.length ? i.useCases : [UNASSIGNED];
      list.forEach((u) => { m[u] = (m[u] || 0) + 1; });
    });
    return m;
  }, [base]);

  const useCaseList = useMemo(() => orderUseCases(Object.keys(useCaseCounts)), [useCaseCounts]);

  const inUseCase = useCallback((item) => {
    if (useCase === 'all') return true;
    const list = item.useCases.length ? item.useCases : [UNASSIGNED];
    return list.indexOf(useCase) !== -1;
  }, [useCase]);

  const typeCounts = useMemo(() => {
    const m = {};
    base.filter(inUseCase).forEach((i) => { m[i.type] = (m[i.type] || 0) + 1; });
    return m;
  }, [base, inUseCase]);

  const visible = useMemo(() => {
    const rows = base
      .filter(inUseCase)
      .filter((i) => typeFilter === 'all' || i.type === typeFilter);
    if (shelf === 'recent') {
      return rows.slice().sort((a, b) => (recentOrder[a.id] || 0) - (recentOrder[b.id] || 0));
    }
    return rows.slice().sort((a, b) => a.title.localeCompare(b.title));
  }, [base, inUseCase, typeFilter, shelf, recentOrder]);

  // Sections: use case is the primary organisation, so "all" renders one block
  // per use case and a report legitimately appears in more than one.
  const sections = useMemo(() => {
    if (useCase !== 'all') return [{ label: useCase, rows: visible }];
    const buckets = {};
    visible.forEach((i) => {
      const list = i.useCases.length ? i.useCases : [UNASSIGNED];
      list.forEach((u) => {
        if (!buckets[u]) buckets[u] = [];
        buckets[u].push(i);
      });
    });
    return orderUseCases(Object.keys(buckets)).map((label) => ({ label, rows: buckets[label] }));
  }, [visible, useCase]);

  // ---- actions ------------------------------------------------------------
  const openItem = useCallback((item) => {
    if (item.broken) {
      setMsg(`"${item.title}" cannot be opened — ${item.brokenReason}`);
      return;
    }
    const recent = [{ id: item.id, at: new Date().toISOString() }]
      .concat(myShelfData.recent.filter((r) => r && r.id !== item.id))
      .slice(0, RECENT_LIMIT);
    writeUser({ recent });
    if (onOpenView) onOpenView(item.viewKey, item.config || null);
  }, [myShelfData.recent, writeUser, onOpenView]);

  const toggleFavorite = useCallback((item) => {
    const next = favSet.has(item.id)
      ? myShelfData.favorites.filter((id) => id !== item.id)
      : myShelfData.favorites.concat([item.id]);
    writeUser({ favorites: next });
  }, [favSet, myShelfData.favorites, writeUser]);

  const closeMenu = () => { setMenuAnchor(null); setMenuItem(null); };

  const newConfig = (fromItem) => {
    const baseKey = (fromItem && BUILT_IN_BY_KEY[fromItem.viewKey]) ? fromItem.viewKey : 'landscape';
    const baseRep = BUILT_IN_BY_KEY[baseKey];
    setCfgDialog({
      id: null,
      viewKey: baseKey,
      name: `${baseRep.title} — View: `,
      description: '',
      useCases: baseRep.useCases.slice(),
      measure: MEASURES[0],
      note: '',
      shared: false,
    });
  };

  const editConfig = (item) => {
    const c = item.record || {};
    setCfgDialog({
      id: c.id,
      viewKey: c.viewKey,
      name: c.name || '',
      description: c.description || '',
      useCases: Array.isArray(c.useCases) ? c.useCases.slice() : [],
      measure: c.measure || MEASURES[0],
      note: c.note || '',
      shared: !!c.shared,
    });
  };

  const saveConfig = () => {
    const d = cfgDialog;
    if (!d || !d.name.trim()) { setMsg('Give the configuration a name before saving.'); return; }
    const now = new Date().toISOString();
    const existing = d.id ? (gallery.configs || []).find((c) => c.id === d.id) : null;
    const record = {
      ...(existing || {}),
      id: d.id || uid(),
      kind: 'gallery-config',
      tenantId,
      viewKey: d.viewKey,
      name: d.name.trim(),
      description: d.description.trim(),
      useCases: d.useCases,
      measure: d.measure,
      note: d.note.trim(),
      shared: !!d.shared,
      // Owner is the signed-in creator or nothing. Never invented.
      owner: existing ? (existing.owner || myEmail || '') : (myEmail || ''),
      createdAt: existing ? (existing.createdAt || now) : now,
      updatedAt: now,
    };
    const configs = (gallery.configs || []).filter((c) => c.id !== record.id).concat([record]);
    persistGallery({ ...gallery, configs });
    setCfgDialog(null);
    setMsg(`Saved "${record.name}".`);
  };

  const deleteConfig = (item) => {
    if (!window.confirm(`Delete the saved configuration "${item.title}"? The base report is untouched.`)) return;
    const configs = (gallery.configs || []).filter((c) => c.id !== item.id);
    persistGallery({ ...gallery, configs });
    setMsg(`Deleted "${item.title}".`);
  };

  const saveLabels = () => {
    const d = labelDialog;
    if (!d) return;
    const labels = { ...(gallery.labels || {}) };
    if (d.useCases.length) labels[d.id] = d.useCases;
    else delete labels[d.id];
    persistGallery({ ...gallery, labels });
    setLabelDialog(null);
    setMsg(`Updated use cases for "${d.title}".`);
  };

  const exportCatalogue = () => {
    const cols = [
      { label: 'Title', value: (r) => r.title },
      { label: 'Report type', value: (r) => r.type },
      { label: 'Use cases', value: (r) => (r.useCases.length ? r.useCases.join('; ') : UNASSIGNED) },
      { label: 'Owner', value: (r) => r.ownerLabel },
      { label: 'Sharing', value: (r) => (r.shared ? 'Shared in this workspace' : 'Private to owner') },
      { label: 'Source', value: (r) => SOURCE_LABEL[r.source] },
      { label: 'Opens view', value: (r) => r.viewKey },
      { label: 'Colour measure', value: (r) => (r.record ? r.record.measure || '' : '') },
      { label: 'Last updated', value: (r) => (r.updated ? fmtDate(r.updated) : 'not recorded') },
      { label: 'Favorite (this user)', value: (r) => (favSet.has(r.id) ? 'yes' : 'no') },
      { label: 'Status', value: (r) => (r.broken ? `broken — ${r.brokenReason}` : 'ok') },
      { label: 'Workspace', value: () => tenantId },
    ];
    exportCsv(`report-catalogue-${tenantId}.csv`, visible, cols);
  };

  const cfgBase = cfgDialog ? BUILT_IN_BY_KEY[cfgDialog.viewKey] : null;

  const toggleIn = (list, value) => (
    list.indexOf(value) === -1 ? list.concat([value]) : list.filter((x) => x !== value)
  );

  // ---- render -------------------------------------------------------------
  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Reports</Typography>
          <Chip size="small" label={tenantId} sx={{ bgcolor: NAVY, color: '#fff', height: 20 }} />
          <Typography variant="caption" sx={{ color: GREY }}>
            {items.length} reports · {BUILT_INS.filter((b) => !b.masterOnly || isMaster).length} built-in ·{' '}
            {userItems.length} authored here
          </Typography>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" sx={primaryBtn} startIcon={<AddIcon />} onClick={() => newConfig(null)}>
            Save a configuration
          </Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCatalogue}>
            Export catalogue ({visible.length})
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 1.5 }}>
        Sharing stops at this workspace. A report saved in <b>{tenantId}</b> is visible only inside{' '}
        <b>{tenantId}</b> — it is either <b>private to its owner</b> or <b>shared with others in this
        workspace</b>. Primordia has no cross-workspace sharing, because tenant isolation is what the
        whole product rests on.
      </Alert>

      {!myEmail && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          No signed-in user, so ownership cannot be attributed. Anything you save here is recorded with
          no owner rather than a guessed one, and favorites and recents are kept for this browser only.
        </Alert>
      )}

      {msg && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={shelf} onChange={(e, v) => setShelf(v)} sx={{ mb: 1.5, borderBottom: '1px solid #e0e0e0' }}>
        {SHELVES.map((s) => (
          <Tab key={s.key} value={s.key} sx={{ fontWeight: 700 }} label={`${s.label} (${shelfCounts[s.key] || 0})`} />
        ))}
      </Tabs>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search title, description or owner…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 300, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField
          select size="small" label="Report type" value={typeFilter} sx={{ minWidth: 240 }}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <MenuItem value="all">All types ({base.filter(inUseCase).length})</MenuItem>
          {Object.keys(typeCounts).sort().map((t) => (
            <MenuItem key={t} value={t}>{t} ({typeCounts[t]})</MenuItem>
          ))}
        </TextField>
      </Stack>

      {shelf === 'mine' && !myEmail && (
        <Alert severity="info" sx={{ mb: 2 }}>
          My Reports lists reports owned by the signed-in user. Nobody is signed in, so this shelf is empty.
        </Alert>
      )}

      {!userItems.length && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderStyle: 'dashed' }}>
          <Typography sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
            No saved reports in this workspace yet
          </Typography>
          <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
            A saved report is a named, owned artefact you can hand over as a deliverable: a base report
            plus the settings you chose, filed under the engagement use case it answers. Two ways to make
            one — build a filtered view or chart in <b>Saved Views &amp; Reports</b>, or save a named
            configuration of any built-in report below (the <i>&ldquo;— View: &hellip;&rdquo;</i> convention,
            so one base report yields several deliverables).
            {fsCount !== null && ` This workspace holds ${fsCount} fact sheets to report on.`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" sx={primaryBtn} startIcon={<AddIcon />} onClick={() => newConfig(null)}>
              Save a configuration
            </Button>
            <Button size="small" variant="outlined" onClick={() => onOpenView && onOpenView('views', null)}>
              Open Saved Views &amp; Reports
            </Button>
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        {/* Use case rail — the primary organisation, the way an engagement is scoped */}
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <Typography variant="overline" sx={{ display: 'block', px: 1.5, pt: 1.5, color: HEAD, fontWeight: 700 }}>
              Engagement use cases
            </Typography>
            <Divider sx={{ mt: 1 }} />
            <List dense disablePadding>
              <ListItemButton selected={useCase === 'all'} onClick={() => setUseCase('all')}>
                <ListItemText
                  primary="All use cases"
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
                />
                <Chip size="small" label={base.length} sx={{ height: 19, fontSize: 10 }} />
              </ListItemButton>
              {useCaseList.map((u) => (
                <ListItemButton key={u} selected={useCase === u} onClick={() => setUseCase(u)}>
                  <ListItemText
                    primary={u}
                    primaryTypographyProps={{ fontSize: 13, color: u === UNASSIGNED ? GREY : undefined }}
                  />
                  <Chip size="small" label={useCaseCounts[u]} sx={{ height: 19, fontSize: 10 }} />
                </ListItemButton>
              ))}
              {!useCaseList.length && (
                <Typography variant="body2" sx={{ color: GREY, p: 2 }}>
                  Nothing matches the current shelf and search.
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Cards */}
        <Grid item xs={12} md={9}>
          {!visible.length && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: GREY }}>
                No report matches this shelf, use case, type and search combination.
              </Typography>
            </Paper>
          )}

          {sections.map((sec) => (
            <Box key={sec.label} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
                  {sec.label}
                </Typography>
                <Chip size="small" label={sec.rows.length} sx={{ height: 19, fontSize: 10 }} />
              </Stack>
              <Grid container spacing={2}>
                {sec.rows.map((item) => (
                  <Grid item xs={12} sm={6} lg={4} key={`${sec.label}:${item.id}`}>
                    <ReportCard
                      item={item}
                      favorite={favSet.has(item.id)}
                      onOpen={openItem}
                      onToggleFavorite={toggleFavorite}
                      onMenu={(anchor, it) => { setMenuAnchor(anchor); setMenuItem(it); }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Grid>
      </Grid>

      {/* ---- card menu ---- */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem
          disabled={!menuItem || menuItem.broken}
          onClick={() => { const it = menuItem; closeMenu(); if (it) openItem(it); }}
        >
          Open report
        </MenuItem>
        <MenuItem onClick={() => { const it = menuItem; closeMenu(); if (it) toggleFavorite(it); }}>
          {menuItem && favSet.has(menuItem.id) ? 'Remove from Favorites' : 'Add to Favorites'}
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!menuItem || !BUILT_IN_BY_KEY[menuItem.viewKey]}
          onClick={() => { const it = menuItem; closeMenu(); newConfig(it); }}
        >
          Save as new configuration…
        </MenuItem>
        <MenuItem
          disabled={!menuItem || menuItem.source !== 'config' || !menuItem.canEdit}
          onClick={() => { const it = menuItem; closeMenu(); if (it) editConfig(it); }}
        >
          Edit configuration…
        </MenuItem>
        <MenuItem
          onClick={() => {
            const it = menuItem;
            closeMenu();
            if (it) {
              setLabelDialog({
                id: it.id,
                title: it.title,
                useCases: (gallery.labels && Array.isArray(gallery.labels[it.id])) ? gallery.labels[it.id].slice() : [],
                declared: it.source === 'builtin' ? (BUILT_IN_BY_KEY[it.viewKey] || {}).useCases || [] : [],
              });
            }
          }}
        >
          <LabelIcon sx={{ fontSize: 16, mr: 1, color: GREY }} /> Add to use cases…
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!menuItem || menuItem.source !== 'config' || !menuItem.canDelete}
          onClick={() => { const it = menuItem; closeMenu(); if (it) deleteConfig(it); }}
        >
          <DeleteIcon sx={{ fontSize: 16, mr: 1, color: GREY }} /> Delete configuration
        </MenuItem>
        {menuItem && (menuItem.source === 'saved-view' || menuItem.source === 'saved-report') && (
          <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: GREY, maxWidth: 300 }}>
            Renaming, re-sharing and deleting a saved view or report happens in Saved Views &amp; Reports,
            which owns that record.
          </Typography>
        )}
      </Menu>

      {/* ---- configuration dialog: the "View:" convention ---- */}
      <Dialog open={!!cfgDialog} onClose={() => setCfgDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: NAVY, fontWeight: 700 }}>
          {cfgDialog && cfgDialog.id ? 'Edit saved configuration' : 'Save a report configuration'}
        </DialogTitle>
        {cfgDialog && (
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                select size="small" label="Base report" value={cfgDialog.viewKey}
                onChange={(e) => {
                  const b = BUILT_IN_BY_KEY[e.target.value];
                  setCfgDialog({
                    ...cfgDialog,
                    viewKey: e.target.value,
                    name: cfgDialog.id ? cfgDialog.name : `${b.title} — View: `,
                    useCases: cfgDialog.id ? cfgDialog.useCases : b.useCases.slice(),
                  });
                }}
              >
                {BUILT_INS.filter((b) => !b.masterOnly || isMaster).map((b) => (
                  <MenuItem key={b.key} value={b.key}>{b.title}</MenuItem>
                ))}
              </TextField>

              <TextField
                size="small" label="Report name" value={cfgDialog.name}
                helperText={'Convention: "<Base report> — View: <colour measure>", so one base report yields several named deliverables.'}
                onChange={(e) => setCfgDialog({ ...cfgDialog, name: e.target.value })}
              />

              <TextField
                size="small" label="Description" value={cfgDialog.description} multiline minRows={2}
                onChange={(e) => setCfgDialog({ ...cfgDialog, description: e.target.value })}
              />

              <TextField
                select size="small" label="Colour measure" value={cfgDialog.measure}
                onChange={(e) => {
                  const m = e.target.value;
                  const suffix = ' — View: ';
                  const nm = cfgDialog.name.indexOf(suffix) !== -1
                    ? `${cfgDialog.name.split(suffix)[0]}${suffix}${m}`
                    : cfgDialog.name;
                  setCfgDialog({ ...cfgDialog, measure: m, name: nm });
                }}
              >
                {MEASURES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>

              <TextField
                size="small" label="Settings note (optional)" value={cfgDialog.note} multiline minRows={2}
                placeholder="e.g. Clinical applications only, phase-out and end-of-life states"
                onChange={(e) => setCfgDialog({ ...cfgDialog, note: e.target.value })}
              />

              <Box>
                <Typography variant="overline" sx={{ color: HEAD, fontWeight: 700 }}>Engagement use cases</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {USE_CASES.map((u) => (
                    <FormControlLabel
                      key={u}
                      sx={{ width: '100%', m: 0 }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={cfgDialog.useCases.indexOf(u) !== -1}
                          onChange={() => setCfgDialog({ ...cfgDialog, useCases: toggleIn(cfgDialog.useCases, u) })}
                        />
                      )}
                      label={<span style={{ fontSize: 13 }}>{u}</span>}
                    />
                  ))}
                </Box>
              </Box>

              <FormControl>
                <Typography variant="overline" sx={{ color: HEAD, fontWeight: 700 }}>Sharing</Typography>
                <RadioGroup
                  value={cfgDialog.shared ? 'shared' : 'private'}
                  onChange={(e) => setCfgDialog({ ...cfgDialog, shared: e.target.value === 'shared' })}
                >
                  <FormControlLabel
                    value="private" control={<Radio size="small" />}
                    label={<span style={{ fontSize: 13 }}>Private to me</span>}
                  />
                  <FormControlLabel
                    value="shared" control={<Radio size="small" />}
                    label={<span style={{ fontSize: 13 }}>Shared with others in the {tenantId} workspace</span>}
                  />
                </RadioGroup>
                <Typography variant="caption" sx={{ color: GREY }}>
                  Those are the only two options. Reports never leave the workspace they were saved in.
                </Typography>
              </FormControl>

              <Alert severity="info">
                The name, description, owner and use cases are stored by this gallery. The colour measure and
                note are handed to <b>{cfgBase ? cfgBase.title : 'the base report'}</b> when it opens; a report
                that does not yet accept a given setting opens at its own default rather than pretending to
                apply it.
              </Alert>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setCfgDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={primaryBtn} onClick={saveConfig}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ---- use case labelling ---- */}
      <Dialog open={!!labelDialog} onClose={() => setLabelDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: NAVY, fontWeight: 700 }}>Use cases</DialogTitle>
        {labelDialog && (
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
              File <b>{labelDialog.title}</b> under the engagement use cases it answers.
            </Typography>
            {!!labelDialog.declared.length && (
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
                Already declared by the report: {labelDialog.declared.join(', ')}.
              </Typography>
            )}
            {USE_CASES.map((u) => (
              <FormControlLabel
                key={u}
                sx={{ width: '100%', m: 0 }}
                control={(
                  <Checkbox
                    size="small"
                    checked={labelDialog.useCases.indexOf(u) !== -1}
                    onChange={() => setLabelDialog({ ...labelDialog, useCases: toggleIn(labelDialog.useCases, u) })}
                  />
                )}
                label={<span style={{ fontSize: 13 }}>{u}</span>}
              />
            ))}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setLabelDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={primaryBtn} onClick={saveLabels}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
